import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { 
  CheckCircle, 
  Warning, 
  XCircle, 
  Clock, 
  ArrowRight,
  Lightbulb,
  ShieldCheck,
  Info
} from '@phosphor-icons/react';

export default function DocumentReviewResult({ 
  reviewData, 
  onRecheck, 
  onContinue,
  isLoading = false 
}) {
  const { language } = useLanguage();

  const translations = {
    en: {
      ready: "Ready to submit",
      readySubtitle: "Your document looks complete and ready",
      almostReady: "Almost ready",
      almostReadySubtitle: "A few things to check before you go",
      notReady: "Fixes needed",
      notReadySubtitle: "Fix these issues to avoid rejection",
      confidenceLabel: "Submission confidence",
      confidenceHelper: "Based on common requirements for this document type",
      whatLooksGood: "What looks good",
      fixFirst: "Fix first",
      fixNext: "Fix next",
      optional: "Optional improvements",
      whatWeSee: "What we usually see",
      nextAction: "Next action",
      processingTime: "Processing time",
      fixAndRecheck: "Fix issues and recheck",
      continueToNext: "Continue to next step",
      uploadNew: "Upload a new document",
      finalLine: "Fixing these now can help you avoid delays or a second visit.",
      disclaimer: "This review checks for completeness only and does not confirm legal validity."
    },
    fr: {
      ready: "Prêt à soumettre",
      readySubtitle: "Votre document semble complet et prêt",
      almostReady: "Presque prêt",
      almostReadySubtitle: "Quelques points à vérifier avant de continuer",
      notReady: "Corrections nécessaires",
      notReadySubtitle: "Corrigez ces problèmes pour éviter un refus",
      confidenceLabel: "Confiance de soumission",
      confidenceHelper: "Basé sur les exigences courantes pour ce type de document",
      whatLooksGood: "Ce qui semble correct",
      fixFirst: "À corriger d'abord",
      fixNext: "À corriger ensuite",
      optional: "Améliorations optionnelles",
      whatWeSee: "Ce qu'on voit généralement",
      nextAction: "Prochaine action",
      processingTime: "Délai de traitement",
      fixAndRecheck: "Corriger et revérifier",
      continueToNext: "Continuer à l'étape suivante",
      uploadNew: "Télécharger un nouveau document",
      finalLine: "Corriger ces points maintenant peut vous éviter des délais ou une deuxième visite.",
      disclaimer: "Cette révision vérifie la complétude uniquement et ne confirme pas la validité juridique."
    }
  };

  const tr = translations[language] || translations.en;

  // Determine status styling
  const getStatusConfig = (status) => {
    switch (status) {
      case 'ready_to_submit':
        return {
          icon: CheckCircle,
          title: tr.ready,
          subtitle: tr.readySubtitle,
          bgColor: 'bg-[#DCFCE7]',
          borderColor: 'border-[#166534]/20',
          iconColor: 'text-[#166534]',
          textColor: 'text-[#166534]'
        };
      case 'needs_fixes':
        return {
          icon: Warning,
          title: tr.almostReady,
          subtitle: tr.almostReadySubtitle,
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          iconColor: 'text-amber-500',
          textColor: 'text-amber-800'
        };
      case 'high_risk':
      default:
        return {
          icon: XCircle,
          title: tr.notReady,
          subtitle: tr.notReadySubtitle,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          iconColor: 'text-red-500',
          textColor: 'text-red-800'
        };
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 80) return 'bg-[#134E4A]';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'blocking':
        return {
          borderColor: 'border-l-red-500',
          bgColor: 'bg-red-50',
          icon: XCircle,
          iconColor: 'text-red-500'
        };
      case 'important':
        return {
          borderColor: 'border-l-amber-500',
          bgColor: 'bg-amber-50',
          icon: Warning,
          iconColor: 'text-amber-500'
        };
      case 'minor':
      default:
        return {
          borderColor: 'border-l-gray-300',
          bgColor: 'bg-gray-50',
          icon: Info,
          iconColor: 'text-gray-400'
        };
    }
  };

  if (!reviewData) return null;

  const statusConfig = getStatusConfig(reviewData.submission_status);
  const StatusIcon = statusConfig.icon;
  const confidenceScore = reviewData.confidence_score || 0;
  const isReady = reviewData.submission_status === 'ready_to_submit';
  const hasBlockingIssues = reviewData.blocking_issues?.length > 0;

  // Get issues grouped by priority
  const blockingIssues = reviewData.blocking_issues || [];
  const importantIssues = reviewData.important_issues || [];
  const minorIssues = reviewData.minor_issues || [];
  const hasAnyIssues = blockingIssues.length > 0 || importantIssues.length > 0 || minorIssues.length > 0;

  return (
    <div className="w-full max-w-[720px] mx-auto" data-testid="document-review-result">
      {/* Status Header Block */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-xl p-6 ${statusConfig.bgColor} ${statusConfig.borderColor} border mb-6`}
        data-testid="review-status-header"
      >
        <div className="flex items-start gap-4">
          <div className={`p-2 rounded-full ${statusConfig.bgColor}`}>
            <StatusIcon size={32} weight="fill" className={statusConfig.iconColor} />
          </div>
          <div>
            <h2 className={`text-xl font-bold ${statusConfig.textColor}`}>
              {reviewData.status_message || statusConfig.title}
            </h2>
            <p className={`text-sm ${statusConfig.textColor} opacity-80 mt-1`}>
              {reviewData.status_subtitle || statusConfig.subtitle}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Confidence Score */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl border border-gray-200 p-6 mb-6"
        data-testid="confidence-score"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">{tr.confidenceLabel}</span>
          <span className={`text-2xl font-bold ${
            confidenceScore >= 80 ? 'text-[#134E4A]' : 
            confidenceScore >= 50 ? 'text-amber-600' : 'text-red-600'
          }`}>
            {confidenceScore}%
          </span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${confidenceScore}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className={`h-full rounded-full ${getConfidenceColor(confidenceScore)}`}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">{tr.confidenceHelper}</p>
      </motion.div>

      {/* SECTION 1: What Looks Good */}
      {reviewData.positive_feedback && reviewData.positive_feedback.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-gray-200 p-6 mb-6"
          data-testid="what-looks-good"
        >
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={20} weight="fill" className="text-[#134E4A]" />
            <h3 className="text-lg font-semibold text-gray-900">{tr.whatLooksGood}</h3>
          </div>
          <ul className="space-y-3">
            {reviewData.positive_feedback.map((item, index) => (
              <li key={index} className="flex items-center gap-3">
                <CheckCircle size={20} weight="fill" className="text-[#134E4A] flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      {/* SECTION 2: Issues Grouped by Priority */}
      {hasAnyIssues && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-gray-200 p-6 mb-6"
          data-testid="issues-section"
        >
          {/* Blocking Issues (Fix First) */}
          {blockingIssues.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <XCircle size={20} weight="fill" className="text-red-500" />
                <h3 className="text-base font-semibold text-red-700">{tr.fixFirst}</h3>
              </div>
              <div className="space-y-3">
                {blockingIssues.map((issue, index) => {
                  const config = getSeverityConfig('blocking');
                  return (
                    <div 
                      key={index} 
                      className={`border-l-4 ${config.borderColor} ${config.bgColor} rounded-r-lg p-4`}
                    >
                      <h4 className="font-medium text-gray-900 mb-1">{issue.issue}</h4>
                      <p className="text-sm text-gray-600 mb-2">{issue.explanation}</p>
                      {issue.impact && (
                        <p className="text-xs text-gray-500 mb-2">
                          <span className="font-medium">Impact:</span> {issue.impact}
                        </p>
                      )}
                      {issue.fix && (
                        <p className="text-sm text-red-700 font-medium flex items-center gap-1">
                          <ArrowRight size={14} />
                          {issue.fix}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Important Issues (Fix Next) */}
          {importantIssues.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Warning size={20} weight="fill" className="text-amber-500" />
                <h3 className="text-base font-semibold text-amber-700">{tr.fixNext}</h3>
              </div>
              <div className="space-y-3">
                {importantIssues.map((issue, index) => {
                  const config = getSeverityConfig('important');
                  return (
                    <div 
                      key={index} 
                      className={`border-l-4 ${config.borderColor} ${config.bgColor} rounded-r-lg p-4`}
                    >
                      <h4 className="font-medium text-gray-900 mb-1">{issue.issue}</h4>
                      <p className="text-sm text-gray-600 mb-2">{issue.explanation}</p>
                      {issue.impact && (
                        <p className="text-xs text-gray-500 mb-2">
                          <span className="font-medium">Impact:</span> {issue.impact}
                        </p>
                      )}
                      {issue.fix && (
                        <p className="text-sm text-amber-700 font-medium flex items-center gap-1">
                          <ArrowRight size={14} />
                          {issue.fix}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Minor Issues (Optional) */}
          {minorIssues.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Info size={20} weight="fill" className="text-gray-400" />
                <h3 className="text-base font-semibold text-gray-600">{tr.optional}</h3>
              </div>
              <div className="space-y-3">
                {minorIssues.map((issue, index) => {
                  const config = getSeverityConfig('minor');
                  return (
                    <div 
                      key={index} 
                      className={`border-l-4 ${config.borderColor} ${config.bgColor} rounded-r-lg p-4`}
                    >
                      <h4 className="font-medium text-gray-900 mb-1">{issue.issue}</h4>
                      <p className="text-sm text-gray-600 mb-2">{issue.explanation}</p>
                      {issue.fix && (
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <ArrowRight size={14} />
                          {issue.fix}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* SECTION 3: What We Usually See (Patterns) */}
      {reviewData.patterns && reviewData.patterns.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-6 mb-6"
          data-testid="patterns-section"
        >
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={20} weight="fill" className="text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900">{tr.whatWeSee}</h3>
          </div>
          <ul className="space-y-3">
            {reviewData.patterns.map((pattern, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-blue-400 mt-1">•</span>
                <span className="text-gray-700 text-sm">{pattern}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      {/* SECTION 4: Next Action & Processing Time */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl border border-gray-200 p-6 mb-6"
        data-testid="next-action-section"
      >
        <div className="space-y-4">
          {reviewData.next_action && (
            <div className="flex items-start gap-3">
              <ArrowRight size={20} className="text-pp-navy mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-sm font-medium text-gray-700">{tr.nextAction}</span>
                <p className="text-sm text-gray-600">{reviewData.next_action}</p>
              </div>
            </div>
          )}
          {reviewData.processing_time && (
            <div className="flex items-start gap-3">
              <Clock size={20} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-sm font-medium text-gray-700">{tr.processingTime}</span>
                <p className="text-sm text-gray-600">{reviewData.processing_time}</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Final Line */}
      {hasAnyIssues && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55 }}
          className="text-sm text-center text-gray-600 mb-4 font-medium"
        >
          {reviewData.final_line || tr.finalLine}
        </motion.p>
      )}

      {/* Disclaimer */}
      <p className="text-xs text-center text-gray-400 mb-6">
        {reviewData.disclaimer || tr.disclaimer}
      </p>

      {/* Primary CTA */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="space-y-3"
      >
        {isReady ? (
          <button
            onClick={onContinue}
            disabled={isLoading}
            className="w-full py-4 px-6 bg-[#134E4A] text-white font-medium rounded-xl hover:bg-[#0F3D3A] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="continue-btn"
          >
            {tr.continueToNext}
            <ArrowRight size={20} />
          </button>
        ) : (
          <>
            <button
              onClick={onRecheck}
              disabled={isLoading}
              className="w-full py-4 px-6 bg-pp-navy text-white font-medium rounded-xl hover:bg-pp-navy-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="recheck-btn"
            >
              {hasBlockingIssues ? tr.uploadNew : tr.fixAndRecheck}
              <ArrowRight size={20} />
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
